import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  signal,
  viewChild,
  ViewEncapsulation,
  OnDestroy,
  effect,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ClientAppHeaderComponent,
  ConversationComponent as SharedConversationComponent,
  HeaderButtonType,
  MessageSenderType,
  FooterButtonType,
  MessageButtonType,
  AssetsPreviewDialogComponent,
  MessageStore,
  ActiveConversationStore,
  ActivityService,
  MessageService,
  TenantStore,
  ConversationStore,
  ConversationHelperService,
  IdentityStore,
  WireApiService,
  ErrorStore,
  MessageButtonClickEvent,
  logger,
  WindowService,
  GlobalErrorComponent,
  AppTypeService,
} from '@frontend/shared';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';

import { ActivatedRoute } from '@angular/router';
import { filter, throwError } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { Conversation, ConversationIncludeOption, Location, Message } from '@clientwire/api';
import { Dialog } from '@angular/cdk/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FilesToUpload } from '@akostm/orbit-ui';
import * as uuid from 'uuid';
import { Title } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { Clipboard } from '@angular/cdk/clipboard';

@Component({
  selector: 'app-conversation',
  standalone: true,
  imports: [
    ClientAppHeaderComponent,
    SharedConversationComponent,
    FormsModule,
    TranslatePipe,
    GlobalErrorComponent,
  ],
  templateUrl: './conversation.component.html',
  styleUrl: './conversation.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.webview-mode]': 'windowService.isWebView()',
  },
})
export class ConversationComponent implements AfterViewInit, OnDestroy {
  //#region Services
  readonly #tenantStore = inject(TenantStore); // eslint-disable-line no-unused-private-class-members
  readonly #wireApiService = inject(WireApiService); // eslint-disable-line no-unused-private-class-members
  readonly windowService = inject(WindowService);
  readonly #messageStore = inject(MessageStore);
  readonly #activeConversationStore = inject(ActiveConversationStore);
  readonly #identityStore = inject(IdentityStore);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #library = inject(FaIconLibrary);
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialog = inject(Dialog);
  readonly #activityService = inject(ActivityService);
  readonly #messageService = inject(MessageService);
  readonly #conversationStore = inject(ConversationStore);
  readonly #conversationHelperService = inject(ConversationHelperService);
  readonly #translateService = inject(TranslateService);
  readonly #errorStore = inject(ErrorStore);
  readonly #titleService = inject(Title);
  readonly #clipboard = inject(Clipboard);
  readonly #toastr = inject(ToastrService);
  readonly #appTypeService = inject(AppTypeService);
  //#endregion

  //#region Variables
  readonly #now = signal<Date>(new Date());
  readonly messageSenderTypeEnum = MessageSenderType;
  readonly #minimumMessageSizeWithGap = 44;
  #dateUpdaterTimeout: number | null = null;
  #messagesInited = false;
  #messagesLength = 0;
  readonly #initErrorCodes = [
    'error.tenantConfig',
    'error.frontends.tenant_not_found_or_disabled',
    'error.http.not_found',
    'error.http.forbidden',
  ];

  readonly #wrongConversationErrorCodes = [
    'error.oidc.invalid_participant_auth_key',
    'error.identity.invalid_credentials',
    'error.oidc.missing_credentials',
    'error.conversations.client_participants.not_found',
  ];
  //#endregion

  //#region Computed
  readonly conversation = computed(() => this.#activeConversationStore.entities()[0]);
  readonly participant = computed(() =>
    this.conversation()?.participants?.find(
      (p) => p.id === this.#identityStore.clientParticipantId()
    )
  );
  readonly conversationAppType = computed(() => {
    const appType = this.#appTypeService.type();
    if (appType === 'main' || appType === 'embed') {
      return appType;
    }
    return 'client';
  });
  readonly #lastReadSequence = computed(() => this.participant()?.lastMessageSequenceSeen ?? 0);
  readonly #unreadMessages = computed(() => this.participant()?.unreadMessagesCount ?? 0);
  readonly #isAtBottom = computed(() => this.conversation()?.scrolledToBottom ?? true);
  readonly messages = computed(() => this.#messageEntity()?.messages ?? []);
  readonly otherTypingParticipants = computed(() => this.#calculateOtherTypingParticipants());
  readonly messagePrependLoading = computed(
    () => this.#messageEntity()?.messagePrependLoading ?? false
  );
  readonly messageAppendLoading = computed(
    () => this.#messageEntity()?.messageAppendLoading ?? false
  );
  readonly #messageEntity = computed(() => {
    const conversationId = this.conversation()?.id;
    if (!conversationId) return;
    return this.#messageStore.entityMap()[conversationId];
  });
  readonly messageIdsWithError = computed(() => this.#messageEntity()?.messageIdsWithError);
  readonly errors = computed(() =>
    this.#errorStore.getErrorsByLocations(this.#messageErrorLocations())
  );
  readonly #messageErrorLocations = computed(() => {
    const conversationId = this.conversation()?.id;
    if (!conversationId) return [];
    return [`${conversationId}:getMessagesOfConversation`];
  });
  readonly initErrors = computed(() =>
    this.#errorStore.getErrorsByCodesAndTypeByPrefix(this.#initErrorCodes, 'global')
  );
  readonly wrongConversation = computed(() =>
    this.#errorStore.getErrorsByCodesAndTypeByPrefix(this.#wrongConversationErrorCodes, 'global')
  );
  //#endregion

  //#region View Child
  readonly conversationComponent = viewChild(SharedConversationComponent);
  //#endregion

  @HostListener('document:visibilitychange', ['$event'])
  async appVisibility() {
    if (!document.hidden) {
      const conversationId = this.conversation()?.id;
      if (conversationId) {
        await this.#fetchMissingMessages();
        if (untracked(this.#isAtBottom)) {
          this.#messageService.setLastUnreadMessage(conversationId);
        }
      }
    }
  }

  constructor() {
    this.#library.addIconPacks(fas);

    // This effect is triggered when the conversation is open and the messages are loaded
    // It check conditions and if all are met, it sets the unread message
    effect(async () => this.#sendMessageReadEventWhenNeeded());

    this.#updateDateSignalToCheck();

    effect(() => {
      this.#setUnreadMessagesToTitle(this.#unreadMessages());
    });
  }

  ngAfterViewInit(): void {
    this.#activatedRoute.params
      .pipe(
        filter((params) => Object.hasOwn(params, 'participantAuthKey')),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((params) => this.initConversation(params['participantAuthKey']));
  }

  ngOnDestroy(): void {
    if (this.#dateUpdaterTimeout !== null) {
      clearTimeout(this.#dateUpdaterTimeout);
    }
  }

  handleHeaderButtonClick(event: HeaderButtonType) {
    console.log('handleButtonClick', event);
  }

  handleSendMessage(event: FooterButtonType) {
    const conversationId = this.conversation()?.id;
    if (!conversationId) {
      throwError(() => new Error(this.#translateService.instant('CONVERSATION.NOT_FOUND')));
      return;
    }
    this.#messageService.sendMessage(event, conversationId);
  }

  handleFilesChange(files?: FilesToUpload[]) {
    if (files) {
      const conversationId = this.conversation()?.id;
      if (!conversationId) {
        throwError(() => new Error(this.#translateService.instant('CONVERSATION.NOT_FOUND')));
        return;
      }
      this.#activeConversationStore.updateConversation(conversationId, { files });
    }
  }

  handleMessageButtonClick(event: MessageButtonClickEvent) {
    switch (event.type) {
      case MessageButtonType.LocationRequest: {
        this.getLocation();
        break;
      }

      case MessageButtonType.CopyCoordinates: {
        this.#copyCoordinates(event.data);
        break;
      }

      case MessageButtonType.OpenGoogleMaps: {
        this.#openGoogleMaps(event.data);
        break;
      }

      default: {
        logger.debug('unhandled event: ', event);
        break;
      }
    }
  }

  #copyCoordinates(coordinates: Location) {
    this.#clipboard.copy(`${coordinates.latitude},${coordinates.longitude}`);
    this.#toastr.success(this.#translateService.instant('SHARED.MESSAGES.COORDINATES_COPIED'));
  }

  #openGoogleMaps(coordinates: Location) {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${coordinates.latitude},${coordinates.longitude}`,
      '_blank'
    );
  }

  refreshMessages() {
    const conversationId = this.conversation()?.id;
    if (!conversationId) return;
    this.#messageStore.getMessagesOfConversation(conversationId, 10, undefined, 'append');
  }

  handleResendMessage(messageId: string) {
    const conversationId = this.conversation()?.id;
    if (!conversationId) return;
    this.#messageService.resendMessage(conversationId, messageId);
  }

  fetchForNewMessages() {
    this.#loadMoreMessages('prepend', 10, true);
  }

  handleDownScroll(): void {
    this.#loadMoreMessages('prepend');
  }

  handleUpScroll(): void {
    this.#loadMoreMessages();
  }

  async #loadMoreMessages(
    position: 'prepend' | 'append' = 'append',
    limit = 10,
    fetchUntilHasMoreMessages = false
  ) {
    this.#conversationHelperService.loadMoreMessages(
      this.#messageEntity(),
      position,
      limit,
      fetchUntilHasMoreMessages
    );
  }

  async initConversation(participant_auth_key: string) {
    // Checking how big is the container and trying to calculate the limit with the most smallest chat messsage
    const chatWrapperHeight = this.conversationComponent()?.chatMessages()?.chatWrapper()
      ?.nativeElement.offsetHeight;
    const limitToHaveScrollbar = Math.ceil(chatWrapperHeight / this.#minimumMessageSizeWithGap);
    // Sign in
    await this.#identityStore.signInWithParticipantAuthKey(participant_auth_key);
    const conversationId = this.#identityStore.clientConversationId();
    if (!conversationId)
      throw new Error(this.#translateService.instant('CONVERSATION.ID_NOT_FOUND'));
    await this.#conversationStore.getConversation(conversationId, [
      ConversationIncludeOption.Participants,
      ConversationIncludeOption.ConversationType,
      ConversationIncludeOption.ClientHeaderTemplateData,
    ]);

    const conversation = this.#conversationStore.entities()[0];
    if (!conversation) throw new Error(this.#translateService.instant('CONVERSATION.NOT_FOUND'));

    // Create conversation message store
    this.#conversationHelperService.addToActiveConversations([conversation]);

    // Subscribe to WebSocket
    this.#conversationHelperService.subscribeToUnreadMessages();
    this.#conversationHelperService.subscribeToConversation(conversationId);

    // // Get messages for the conversation
    await this.#messageStore.getMessagesOfConversation(conversation.id, limitToHaveScrollbar);
  }

  handleScrolledToBottom(event: boolean) {
    const conversation = this.conversation();
    if (!conversation) return;
    this.#setActiveConversationScrollState(conversation, event);
  }

  #setActiveConversationScrollState(conversation: Conversation, scrolledToBottom: boolean) {
    this.#activeConversationStore.updateConversation(conversation.id, { scrolledToBottom });
  }

  handleMessageRead(message: Message) {
    this.#activityService.handleMessageSeenActivity(message);
  }

  getLocation() {
    this.openLocationSelectorOverlay();
  }

  openLocationSelectorOverlay(): void {
    const conversationId = this.#messageEntity()?.id;
    if (!conversationId) throw new Error(this.#translateService.instant('CONVERSATION.NOT_FOUND'));
    this.#conversationHelperService.openLocationSelectorOverlay(
      conversationId,
      this.conversation()?.messageSourceId
    );
  }

  handleAssetClick(messageId: string) {
    this.#openAssetDialog(messageId);
  }

  #openAssetDialog(messageId: string) {
    const messageEntity = this.#messageEntity();
    if (!messageEntity) throw new Error(this.#translateService.instant('CONVERSATION.NOT_FOUND'));

    const messages = messageEntity.messages;
    const message = messages.find((msg) => msg.id === messageId);
    this.#dialog.open(AssetsPreviewDialogComponent, {
      panelClass: 'fullscreen-dialog',
      backdropClass: 'fullscreen-dialog-backdrop',
      data: {
        messages: messages,
        selectedMessage: message,
      },
    });
  }

  handleIsOpenEvent(stillOpen: boolean) {
    const conversationId = this.#messageEntity()?.id;
    if (!conversationId) {
      throwError(() => new Error(this.#translateService.instant('CONVERSATION.NOT_FOUND')));
      return;
    }
    if (stillOpen) {
      this.#activityService.handleConversationOpenActivity(conversationId);
    }
  }

  sendIsTypingEvent(event: boolean) {
    const conversationId = this.#messageEntity()?.id;
    if (!conversationId) {
      throwError(() => new Error(this.#translateService.instant('CONVERSATION.NOT_FOUND')));
      return;
    }
    if (event) {
      if (!this.conversation()?.messageSourceId) {
        this.#activeConversationStore.setNewMessageSourceId(conversationId, uuid.v4());
      }
      this.#activityService.handleIsTypingActivity(
        conversationId,
        'me',
        this.conversation()?.messageSourceId
      );
    }
  }

  reload() {
    window.location.reload();
  }

  // This function is triggering the computed signals to compute again
  #updateDateSignalToCheck() {
    this.#now.set(new Date());
    if (this.#dateUpdaterTimeout !== null) {
      clearTimeout(this.#dateUpdaterTimeout);
    }
    this.#dateUpdaterTimeout = window.setTimeout(() => {
      this.#updateDateSignalToCheck();
    }, 1000);
  }

  #calculateOtherTypingParticipants() {
    const conversation = this.conversation();
    const messages = this.messages();
    if (!conversation) return undefined;
    const otherParticipants = conversation.participants?.filter((p) => {
      const activeUserParticipantId = this.#activityService.findParticipantIdForConversation(
        conversation.id
      );
      return p.id !== activeUserParticipantId;
    });
    const lastMessage = messages[0];
    return otherParticipants?.filter((p) => {
      if (p.sourceId) {
        return (
          this.#now().getTime() - (p.wasTypingAt?.getTime() ?? 0) < 5000 &&
          lastMessage?.sourceId !== p.sourceId
        );
      }
      return this.#now().getTime() - (p.wasTypingAt?.getTime() ?? 0) < 5000;
    });
  }

  async #sendMessageReadEventWhenNeeded() {
    const atBottom = untracked(this.#isAtBottom);
    const lastReadSequence = untracked(this.#lastReadSequence);
    const messageEntity = this.#messageEntity();
    if (
      !messageEntity ||
      (messageEntity.lastUpdateEvent !== 'serverMessages' &&
        messageEntity.lastUpdateEvent !== 'socketMessage')
    )
      return;

    const msgs = messageEntity.messages;
    if (msgs.length !== this.#messagesLength) {
      this.#messagesLength = msgs.length;
    } else {
      return;
    }
    // If not in focus or not scrolled to bottom, do nothing
    if (!atBottom || !msgs?.length) return;

    const newestMsg = msgs[0];
    if (!newestMsg) return;
    const highestSequence = newestMsg?.sequence ?? 0;

    // 1) first time we arrive (conversation is open & scrolled bottom)
    if (!this.#messagesInited) {
      this.#messagesInited = true;
      if (highestSequence > lastReadSequence) {
        await this.#messageService.setUnreadMessage(newestMsg);
      }
      return;
    }

    // 2) subsequent runs => if the newest message sequence is bigger than last time
    if (highestSequence > lastReadSequence) {
      await this.#messageService.setUnreadMessage(newestMsg);
    }
  }

  #setUnreadMessagesToTitle(unreadMessages: number) {
    if (unreadMessages) {
      this.#titleService.setTitle(
        this.#translateService.instant('SHARED.GENERAL.UNREAD_MESSAGES', { value: unreadMessages })
      );
    } else {
      this.#titleService.setTitle(this.#translateService.instant('CLIENT_APP_TITLE'));
    }
  }

  async #fetchMissingMessages() {
    const conversationId = this.conversation()?.id;
    if (!conversationId) return;
    const lastMessageSquence =
      this.#messageStore.entityMap()[conversationId]?.messages[0]?.sequence;
    if (!lastMessageSquence) return;
    await this.#messageStore.getMessagesOfConversation(
      conversationId,
      10,
      lastMessageSquence,
      'prepend',
      undefined,
      'ASC',
      true
    );
  }
}
